import { useEffect, useLayoutEffect, useState } from "react";

/*
|--------------------------------------------------------------------------
| Mobile-device detection
|--------------------------------------------------------------------------
|
| The guard is intended for phones only. Tablets and desktop windows should
| remain usable in landscape mode. We prefer the browser's explicit mobile
| hint and keep a conservative user-agent fallback for Safari and older
| browsers.
|
*/
const isPhoneDevice = () => {
  if (typeof window === "undefined") {
    return false;
  }

  if (typeof navigator.userAgentData?.mobile === "boolean") {
    return navigator.userAgentData.mobile;
  }

  const userAgent = navigator.userAgent || "";

  const isKnownPhone =
    /iPhone|iPod|Windows Phone|IEMobile|Opera Mini/i.test(userAgent) ||
    /Android.*Mobile/i.test(userAgent) ||
    /Mobile/i.test(userAgent);

  if (isKnownPhone) {
    return true;
  }

  /*
  |------------------------------------------------------------------------
  | Conservative fallback
  |------------------------------------------------------------------------
  |
  | A coarse pointer plus a small shortest screen edge normally represents a
  | phone. The small limit deliberately avoids applying the guard to tablets.
  |
  */
  const hasCoarsePointer = window.matchMedia?.("(pointer: coarse)").matches;
  const shortestScreenEdge = Math.min(
    window.screen?.width || window.innerWidth,
    window.screen?.height || window.innerHeight
  );

  return Boolean(hasCoarsePointer && shortestScreenEdge <= 600);
};

/*
|--------------------------------------------------------------------------
| Rotation readability thresholds
|--------------------------------------------------------------------------
|
| These are the only two layout thresholds used by the adaptive tablet/fold
| rotation rule.
|
| - Width >= 840px gives the feed enough horizontal room for the current UI.
| - Height >= 600px keeps the landscape layout usable without rejecting
|   smaller tablets such as iPad mini or large unfolded foldables.
|
*/
const ROTATION_MIN_WIDTH = 840;
const ROTATION_MIN_HEIGHT = 600;

const getViewportDimensions = () => {
  if (typeof window === "undefined") {
    return {
      width: 0,
      height: 0,
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

const isLandscapeViewport = () => {
  const { width, height } = getViewportDimensions();

  if (width <= 0 || height <= 0) {
    return false;
  }

  const orientationQuery = window.matchMedia?.("(orientation: landscape)");

  if (orientationQuery) {
    return orientationQuery.matches;
  }

  return width > height;
};

const hasTouchInput = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
  const hasCoarsePointer =
    window.matchMedia?.("(pointer: coarse)").matches === true;

  return maxTouchPoints > 0 || hasCoarsePointer;
};

const canSatisfyAdaptiveThresholds = () => {
  const { width, height } = getViewportDimensions();

  const shortEdge = Math.min(width, height);
  const longEdge = Math.max(width, height);

  /*
  |------------------------------------------------------------------------
  | Avoid impossible rotation loops
  |------------------------------------------------------------------------
  |
  | Only use the two-threshold rotation rule when at least one orientation can
  | satisfy both thresholds. If the device is physically too small, rotation
  | cannot solve the layout and the adaptive guard stays out of the way.
  |
  */
  return (
    shortEdge >= ROTATION_MIN_HEIGHT &&
    longEdge >= ROTATION_MIN_WIDTH
  );
};

const getOrientationGuardTarget = () => {
  const { width, height } = getViewportDimensions();

  if (width <= 0 || height <= 0) {
    return null;
  }

  /*
  |------------------------------------------------------------------------
  | Existing phone policy
  |------------------------------------------------------------------------
  |
  | Compact phones keep the existing portrait-only behavior. This prevents a
  | tall phone from being mistaken for a tablet just because its height is
  | greater than ROTATION_MIN_HEIGHT.
  |
  */
  const isCompactPhone =
    isPhoneDevice() &&
    Math.min(width, height) < ROTATION_MIN_HEIGHT;

  if (isCompactPhone) {
    return isLandscapeViewport() ? "portrait" : null;
  }

  /*
  |------------------------------------------------------------------------
  | Tablet / unfolded foldable policy
  |------------------------------------------------------------------------
  |
  | The rule is based on the real viewport, not a device model:
  |
  | width OK + height OK  -> no rotation message
  | width NO + height OK  -> landscape required
  | width OK + height NO  -> portrait required
  |
  | Non-touch desktop windows are never orientation-guarded.
  |
  */
  if (!hasTouchInput() || !canSatisfyAdaptiveThresholds()) {
    return null;
  }

  const widthIsReadable = width >= ROTATION_MIN_WIDTH;
  const heightIsReadable = height >= ROTATION_MIN_HEIGHT;

  if (widthIsReadable && heightIsReadable) {
    return null;
  }

  if (!widthIsReadable && heightIsReadable) {
    return "landscape";
  }

  if (widthIsReadable && !heightIsReadable) {
    return "portrait";
  }

  return null;
};

export function usePortraitOrientationGuard() {
  const [guardTarget, setGuardTarget] = useState(
    getOrientationGuardTarget
  );

  useEffect(() => {
    const orientationQuery = window.matchMedia?.("(orientation: landscape)");

    const updateOrientationState = () => {
      setGuardTarget(getOrientationGuardTarget());
    };

    updateOrientationState();

    window.addEventListener("resize", updateOrientationState, {
      passive: true,
    });

    window.addEventListener("orientationchange", updateOrientationState, {
      passive: true,
    });

    if (orientationQuery?.addEventListener) {
      orientationQuery.addEventListener("change", updateOrientationState);
    } else if (orientationQuery?.addListener) {
      orientationQuery.addListener(updateOrientationState);
    }

    return () => {
      window.removeEventListener("resize", updateOrientationState);
      window.removeEventListener("orientationchange", updateOrientationState);

      if (orientationQuery?.removeEventListener) {
        orientationQuery.removeEventListener("change", updateOrientationState);
      } else if (orientationQuery?.removeListener) {
        orientationQuery.removeListener(updateOrientationState);
      }
    };
  }, []);

  /*
  | Apply the guard class before the browser paints the new orientation.
  | This closes the small compositor race in iOS Safari where a fixed ticker
  | layer could briefly be painted above the guard.
  */
  useLayoutEffect(() => {
    const rootElement = document.documentElement;
    const bodyElement = document.body;

    rootElement.classList.toggle(
      "orientation-guard-active",
      Boolean(guardTarget)
    );

    bodyElement.classList.toggle(
      "orientation-guard-active",
      Boolean(guardTarget)
    );

    return () => {
      rootElement.classList.remove("orientation-guard-active");
      bodyElement.classList.remove("orientation-guard-active");
    };
  }, [guardTarget]);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    const screenOrientation = window.screen?.orientation;

    if (!isStandalone || !screenOrientation) {
      return undefined;
    }

    /*
    |------------------------------------------------------------------------
    | Best-effort PWA orientation policy
    |------------------------------------------------------------------------
    |
    | Keep the native orientation request synchronized with the exact same
    | viewport-threshold logic used by the visible guard.
    |
    */
    const applyOrientationPolicy = () => {
      const { width, height } = getViewportDimensions();
      const isCompactPhone =
        isPhoneDevice() &&
        Math.min(width, height) < ROTATION_MIN_HEIGHT;

      const targetOrientation = getOrientationGuardTarget();

      if (targetOrientation) {
        screenOrientation
          .lock?.(targetOrientation)
          .catch(() => {});

        return;
      }

      if (isCompactPhone) {
        screenOrientation.lock?.("portrait").catch(() => {});
        return;
      }

      screenOrientation.unlock?.();
    };

    applyOrientationPolicy();

    window.addEventListener("resize", applyOrientationPolicy, {
      passive: true,
    });

    window.addEventListener("orientationchange", applyOrientationPolicy, {
      passive: true,
    });

    return () => {
      window.removeEventListener("resize", applyOrientationPolicy);
      window.removeEventListener("orientationchange", applyOrientationPolicy);
    };
  }, []);

  return guardTarget;
}
